import { useNavigate } from "react-router-dom"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/shadcn/ui/alert-dialog"
import { Button } from "~/shadcn/ui/button"
import { publicRosterPath } from "~/common/paths"
import toast from "react-hot-toast"
import { announceGeneratedMediaKit, publicMediaKitUrl } from "./mediaKitLink"
import { usePublicMediaKitUpdate } from "./usePublicMediaKitUpdate"

// Who is publishing: the agency itself ("Generate", ZAN-929), or a site admin
// switching someone else's agency from the admin screens.
export type PublishMediaKitVariant =
  | { kind: "own"; agencySlug: string }
  | { kind: "admin"; agencyName: string }

type DialogProps = {
  agencyId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onPublished?: () => void
}

// Confirms before an agency's roster and media kits become public. Stays open
// if publishing fails, so the user can retry.
export const PublishMediaKitDialog = ({
  variant,
  ...props
}: DialogProps & { variant: PublishMediaKitVariant }) => {
  switch (variant.kind) {
    case "own":
      return (
        <GenerateOwnMediaKitDialog agencySlug={variant.agencySlug} {...props} />
      )
    case "admin":
      return (
        <AdminPublishMediaKitDialog
          agencyName={variant.agencyName}
          {...props}
        />
      )
  }
}

// Sapir's copy (ZAN-929). Once generated, the agency sees its URL and lands
// on its media kit, unless visitors can't see it yet (no active plan), in
// which case it would land on a not-found page.
const GenerateOwnMediaKitDialog = ({
  agencyId,
  agencySlug,
  open,
  onOpenChange,
  onPublished,
}: DialogProps & { agencySlug: string }) => {
  const { setPublished, loading } = usePublicMediaKitUpdate()
  const navigate = useNavigate()

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Generate Your Media Kit</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="flex flex-col gap-3">
              <p>
                Turn your Zano roster into a public media kit you can share
                outside the platform.
              </p>
              <p>
                Anyone with the link will be able to view your roster and the
                individual media kits of your visible creators. Once generated,
                you'll be taken directly to your new media kit, ready to view
                and share.
              </p>
              <p>You can unpublish it at any time in Account Settings.</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          <Button
            disabled={loading}
            onClick={async () => {
              const result = await setPublished(agencyId, true)
              if (!result.ok) return
              onOpenChange(false)
              onPublished?.()
              announceGeneratedMediaKit({
                url: publicMediaKitUrl(agencySlug),
                publiclyViewable: result.publiclyViewable,
              })
              if (result.publiclyViewable) {
                navigate(publicRosterPath({ agencySlug }))
              }
            }}
          >
            Generate Media Kit
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

const AdminPublishMediaKitDialog = ({
  agencyId,
  agencyName,
  open,
  onOpenChange,
  onPublished,
}: DialogProps & { agencyName: string }) => {
  const { setPublished, loading } = usePublicMediaKitUpdate()

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{`Publish ${agencyName}'s media kit?`}</AlertDialogTitle>
          <AlertDialogDescription>
            Anyone with the link will be able to see this agency's roster and
            the media kit of each visible creator. The agency's admins can
            unpublish it in Settings.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          <Button
            disabled={loading}
            onClick={async () => {
              const result = await setPublished(agencyId, true)
              if (!result.ok) return
              onOpenChange(false)
              onPublished?.()
              toast.success("Media kit published")
            }}
          >
            Publish media kit
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
